import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "../utils/Toast";
import { CheckCircle, Clock, AlertTriangle, TrendingUp, History, Users, CalendarClock } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { isTaskOverdue } from "../utils/taskHelpers";

const EMOJI_TIPO = {
  COMMESSA_CREATA:"📁", COMMESSA_CHIUSA:"✅", COMMESSA_RIAPERTA:"🔓", COMMESSA_ARCHIVIATA:"🗄️",
  TASK_CREATO:"📌", TASK_COMPLETATO:"✔️", TASK_ASSEGNATO:"👤", TASK_IN_RITARDO:"⏰",
  VERBALE_CREATO:"📝", VERBALE_TASK_GENERATI:"📝",
  COMUNICAZIONE_REGISTRATA:"📨", RAPPORTINO_CREATO:"📋",
  NOTA_SPESE_INVIATA:"💳", NOTA_SPESE_APPROVATA:"✅", NOTA_SPESE_RESPINTA:"❌",
};

function formattaQuando(ts) {
  if (!ts?.toDate) return "—";
  const d = ts.toDate();
  const oggi = new Date(); oggi.setHours(0,0,0,0);
  const dGiorno = new Date(d); dGiorno.setHours(0,0,0,0);
  const diffGiorni = Math.round((oggi - dGiorno) / 86400000);
  const ora = d.toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"});
  if (diffGiorni===0) return `oggi alle ${ora}`;
  if (diffGiorni===1) return `ieri alle ${ora}`;
  if (diffGiorni<7) return `${diffGiorni} giorni fa`;
  return d.toLocaleDateString("it-IT")+` alle ${ora}`;
}

function giorniTra(a, b) {
  const g = Math.round((b - a) / 86400000);
  return g;
}

export default function ProjectDashboard({ project }) {
  const { addToast } = useToast();
  const [tasks, setTasks] = useState([]);
  const [eventiRecenti, setEventiRecenti] = useState([]);

  useEffect(() => {
    if (!project?.organizationId) return; // aperta solo per id: aspetto i dati
    const q = query(collection(db,"tasks"),where("projectId","==",project.id),where("organizationId","==",project.organizationId));
    return onSnapshot(q, snap=>setTasks(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [project.id, project.organizationId]);

  // Ultimi eventi della commessa (Smart Overview — riusa la collezione "eventi" già scritta da altri moduli)
  useEffect(() => {
    if (!project?.organizationId) return; // aperta solo per id: aspetto i dati
    const q = query(collection(db,"eventi"), where("projectId","==",project.id), where("orgId","==",project.organizationId));
    return onSnapshot(q, snap=>{
      const lista = snap.docs.map(d=>({id:d.id,...d.data()}));
      lista.sort((a,b)=>(b.createdAt?.toMillis?.()||0)-(a.createdAt?.toMillis?.()||0));
      setEventiRecenti(lista.slice(0,5));
    }, err=>console.warn("eventi recenti:",err));
  }, [project.id, project.organizationId]);

  const today = toLocalDateStr(new Date());
  const total = tasks.length;
  const completate = tasks.filter(t=>t.status==="completata").length;
  const aperte = tasks.filter(t=>t.status==="aperta").length;
  const inCorso = tasks.filter(t=>t.status==="in_corso").length;
  const scadute = tasks.filter(isTaskOverdue).length; // W452: regola unica (utils/taskHelpers.js)
  const progress = total>0 ? Math.round(completate/total*100) : 0;

  // Traguardi 50%/100% — piccolo toast celebrativo, una sola volta per commessa (memoria solo nel browser)
  useEffect(() => {
    if (total===0) return;
    const chiave = `nexiora_milestone_${project.id}`;
    let gia = [];
    try { gia = JSON.parse(localStorage.getItem(chiave)||"[]"); } catch(e) { gia = []; }
    if (progress>=100 && !gia.includes(100)) {
      addToast(`🎉 "${project.name}" — tutte le attività completate!`,"success");
      localStorage.setItem(chiave, JSON.stringify([...gia,100]));
    } else if (progress>=50 && !gia.includes(50)) {
      addToast(`🎉 "${project.name}" — metà lavoro fatto!`,"success");
      localStorage.setItem(chiave, JSON.stringify([...gia,50]));
    }
  }, [progress, total, project.id]);

  // Per membro
  const byMember = {};
  tasks.forEach(t=>{
    if (t.assignedName) {
      if (!byMember[t.assignedName]) byMember[t.assignedName]={name:t.assignedName,total:0,completate:0};
      byMember[t.assignedName].total++;
      if (t.status==="completata") byMember[t.assignedName].completate++;
    }
  });

  // Tempo di vita della commessa
  const dataApertura = project.createdAt?.toDate ? project.createdAt.toDate() : null;
  const chiusa = project.statoCommessa === "chiusa";
  let giorniVita = null, durataChiusa = null;
  if (dataApertura) {
    if (chiusa && project.dataChiusura) {
      durataChiusa = giorniTra(dataApertura, new Date(project.dataChiusura+"T00:00:00"));
    } else {
      giorniVita = giorniTra(dataApertura, new Date());
    }
  }

  // Persone coinvolte (dai membri della commessa)
  const membri = project.membri || [];

  // "Compleanno" della commessa — solo se in corso, sui multipli tondi
  const traguardiGiorni = [30,100,365,730,1095];
  const compleanno = (!chiusa && giorniVita!==null && traguardiGiorni.includes(giorniVita)) ? giorniVita : null;

  // Confronto completate questo mese vs mese scorso (da task.completedAt)
  const oraD = new Date();
  const meseCorrente = oraD.getMonth(), annoCorrente = oraD.getFullYear();
  const meseScorsoD = new Date(annoCorrente, meseCorrente-1, 1);
  const meseScorso = meseScorsoD.getMonth(), annoMeseScorso = meseScorsoD.getFullYear();
  let completateQuestoMese = 0, completateMeseScorso = 0;
  tasks.forEach(t=>{
    if (t.status!=="completata" || !t.completedAt) return;
    const d = t.completedAt?.toDate ? t.completedAt.toDate() : new Date(t.completedAt);
    if (isNaN(d)) return;
    if (d.getFullYear()===annoCorrente && d.getMonth()===meseCorrente) completateQuestoMese++;
    else if (d.getFullYear()===annoMeseScorso && d.getMonth()===meseScorso) completateMeseScorso++;
  });
  const deltaMese = completateQuestoMese - completateMeseScorso;

  return (
    <div className="content-scroll" style={{padding:20}}>

      {/* ── Smart Overview ── */}
      <div style={{display:"grid",gridTemplateColumns:"1.3fr 1fr",gap:12,marginBottom:16}}>

        {/* Ultimi aggiornamenti */}
        <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16}}>
          <div style={{display:"flex",alignItems:"center",gap:6,fontSize:13,fontWeight:600,marginBottom:10}}>
            <History size={15} color="#7c3aed"/> Ultimi aggiornamenti
          </div>
          {eventiRecenti.length===0 ? (
            <div style={{fontSize:12,color:"#9ca3af"}}>Nessun evento registrato ancora per questa commessa.</div>
          ) : eventiRecenti.map(ev=>(
            <div key={ev.id} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:8,paddingBottom:8,borderBottom:"1px solid #f3f4f6"}}>
              <span style={{fontSize:15,flexShrink:0}}>{EMOJI_TIPO[ev.tipo]||"•"}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,color:"#374151"}}>{ev.titolo}</div>
                <div style={{fontSize:11,color:"#9ca3af"}}>{formattaQuando(ev.createdAt)} · {ev.nomeUtente||"Sistema"}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {/* Tempo di vita della commessa */}
          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16}}>
            <div style={{display:"flex",alignItems:"center",gap:6,fontSize:13,fontWeight:600,marginBottom:8}}>
              <CalendarClock size={15} color="#0369a1"/> Tempo di vita
            </div>
            {dataApertura ? (
              <>
                <div style={{fontSize:11,color:"#9ca3af"}}>Aperta il {dataApertura.toLocaleDateString("it-IT")}</div>
                {chiusa && durataChiusa!==null ? (
                  <div style={{fontSize:20,fontWeight:800,color:"#374151",marginTop:2}}>{durataChiusa} giorni <span style={{fontSize:12,fontWeight:500,color:"#9ca3af"}}>(chiusa)</span></div>
                ) : giorniVita!==null ? (
                  <div style={{fontSize:20,fontWeight:800,color:"#374151",marginTop:2}}>{giorniVita} giorni <span style={{fontSize:12,fontWeight:500,color:"#9ca3af"}}>in corso</span></div>
                ) : null}
                {compleanno!==null && (
                  <div style={{marginTop:6,fontSize:11,fontWeight:700,color:"#d97706",background:"#fef3c7",display:"inline-block",padding:"2px 8px",borderRadius:10}}>
                    🎂 Oggi compie {compleanno} giorni!
                  </div>
                )}
              </>
            ) : <div style={{fontSize:12,color:"#9ca3af"}}>Data apertura non disponibile</div>}
          </div>

          {/* Persone coinvolte */}
          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16,flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:6,fontSize:13,fontWeight:600,marginBottom:8}}>
              <Users size={15} color="#059669"/> Persone coinvolte
            </div>
            {membri.length===0 ? (
              <div style={{fontSize:12,color:"#9ca3af"}}>Nessun membro assegnato</div>
            ) : (
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {membri.map(m=>(
                  <div key={m.uid} title={m.ruolo} style={{display:"flex",alignItems:"center",gap:5,background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:20,padding:"3px 10px 3px 3px"}}>
                    <div className="avatar" style={{width:20,height:20,fontSize:10}}>{m.nome?.[0]?.toUpperCase()}</div>
                    <span style={{fontSize:11,fontWeight:500}}>{m.nome}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress generale */}
      <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:20,marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <span style={{fontSize:14,fontWeight:600}}>Progresso generale</span>
          <span style={{fontSize:22,fontWeight:800,color:"#7c3aed"}}>{progress}%</span>
        </div>
        <div className="progress-bar" style={{height:10}}>
          <div className="progress-bar-fill" style={{width:`${progress}%`,background:"#7c3aed"}}/>
        </div>
        <div style={{fontSize:12,color:"#9ca3af",marginTop:6}}>{completate} di {total} attività completate</div>
        {(completateQuestoMese>0 || completateMeseScorso>0) && (
          <div style={{fontSize:12,color:"#6b7280",marginTop:4}}>
            Questo mese: <strong style={{color:"#374151"}}>{completateQuestoMese}</strong> attività completate
            {completateMeseScorso>0 && (
              <span style={{color: deltaMese>=0 ? "#059669" : "#dc2626", fontWeight:600}}>
                {" "}({deltaMese>=0?"+":""}{deltaMese} rispetto al mese scorso)
              </span>
            )}
          </div>
        )}
      </div>

      {/* Stats grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
        {[
          {icon:TrendingUp,label:"Totale",value:total,color:"#7c3aed"},
          {icon:CheckCircle,label:"Completate",value:completate,color:"#059669"},
          {icon:Clock,label:"In corso",value:aperte+inCorso,color:"#d97706"},
          {icon:AlertTriangle,label:"Scadute",value:scadute,color:"#dc2626"},
        ].map(s=>(
          <div key={s.label} className="stat-card">
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
              <s.icon size={16} color={s.color}/>
              <span className="stat-label">{s.label}</span>
            </div>
            <div className="stat-val" style={{color:s.color}}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Per priorità */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16}}>
          <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>Per priorità</div>
          {[
            {label:"Alta",count:tasks.filter(t=>t.priority==="alta").length,color:"#dc2626",bg:"#fee2e2"},
            {label:"Media",count:tasks.filter(t=>t.priority==="media").length,color:"#d97706",bg:"#fef3c7"},
            {label:"Bassa",count:tasks.filter(t=>t.priority==="bassa").length,color:"#059669",bg:"#d1fae5"},
          ].map(p=>(
            <div key={p.label} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
              <span style={{width:60,fontSize:12,color:p.color,fontWeight:600}}>{p.label}</span>
              <div style={{flex:1,height:8,background:"#f3f4f6",borderRadius:10,overflow:"hidden"}}>
                <div style={{width:total>0?`${Math.round(p.count/total*100)}%`:"0%",height:"100%",background:p.color,borderRadius:10}}/>
              </div>
              <span style={{fontSize:12,color:"#6b7280",width:24,textAlign:"right"}}>{p.count}</span>
            </div>
          ))}
        </div>

        <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16}}>
          <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>Per membro</div>
          {Object.values(byMember).length===0 ? (
            <div style={{fontSize:13,color:"#9ca3af"}}>Nessuna attività assegnata</div>
          ) : Object.values(byMember).map(m=>(
            <div key={m.name} style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
              <div className="avatar" style={{width:24,height:24,fontSize:11}}>{m.name[0]?.toUpperCase()}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:500}}>{m.name}</div>
                <div style={{fontSize:11,color:"#9ca3af"}}>{m.completate}/{m.total} completate</div>
              </div>
              <div className="progress-bar" style={{width:60}}>
                <div className="progress-bar-fill" style={{width:m.total>0?`${Math.round(m.completate/m.total*100)}%`:"0%"}}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
